import { reactive, ref } from '@vue/reactivity';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { ONLINE_STATE_CHANGED, AUTH_STATE_CHANGED } from './constants';
import type { RootStore } from './rootStore';
import type { ServiceStore } from './serviceStore';
import type { TFeathersClient, IKeyValueStore, TApiError } from './types';
import { Console } from './utils/debugger';

interface IAuthStoreState {
  data: any;
  token: null | string;
}

export interface IAuthStoreOptions {
  feathers: TFeathersClient;
  storage: IKeyValueStore;
  userService?: ServiceStore<any>;
  // hooks
  afterAuthenticate?: (token?: string) => void;
  beforeLogout?: () => void;
  afterLogout?: () => void;
  config?: {
    expireFormat?: 'seconds' | 'miliseconds';
    debug: boolean;
  };
}

export class AuthStore<UserModel> {
  state: IAuthStoreState = reactive({
    token: null,
    data: null,
  });
  storageKey: string;
  onAuthentication?: (token?: string | null) => void;
  client: TFeathersClient;
  storage: IKeyValueStore;
  // @ts-ignore
  userService?: ServiceStore<UserModel>;
  expireFormat: 'seconds' | 'miliseconds' = 'seconds';
  rootStore: RootStore;
  isAuthPending = ref(false);
  authError = ref<TApiError>(undefined);
  debug = false;
  console: ReturnType<typeof Console>;
  // hooks
  afterAuthenticate = (token?: string) => {
    this.console.log('Hook afterAuthenticate', token);
  };
  beforeLogout = () => {
    this.console.log('Hook beforeLogout');
  };
  afterLogout = () => {
    this.console.log('Hook afterLogout');
  };

  constructor(rootStore: RootStore, options: IAuthStoreOptions) {
    this.storageKey = '@storage/authentication';
    this.rootStore = rootStore;
    this.client = options.feathers;
    this.storage = options.storage;
    this.userService = options.userService;

    if (options.config) {
      this.expireFormat = options.config.expireFormat || 'seconds';
      this.debug = options.config.debug || false;
    }

    if (options.afterAuthenticate) {
      this.afterAuthenticate = options.afterAuthenticate;
    }
    if (options.beforeLogout) {
      this.beforeLogout = options.beforeLogout;
    }
    if (options.afterLogout) {
      this.afterLogout = options.afterLogout;
    }

    this.console = Console(this.debug);
    this.initialize();
    this.handleEvents();
  }

  handleEvents() {
    const onlineStateChanged = async (isOnline?: boolean) => {
      if (isOnline) {
        const authStore = await this.storage.getItem(this.storageKey);
        const { token } = JSON.parse(authStore);
        const { exp } = jwtDecode<JwtPayload>(token);
        this.reAuthenticate(true, exp);
      }
    };

    this.console.log('Auth is listening to RootStore events.');
    this.rootStore.emitter.off<boolean>(ONLINE_STATE_CHANGED, (e) => onlineStateChanged(e));
    this.rootStore.emitter.on<boolean>(ONLINE_STATE_CHANGED, (e) => onlineStateChanged(e));
  }

  getNow() {
    let now = new Date().getTime();
    if (this.expireFormat === 'seconds') {
      now = Math.floor(now / 1000);
    }

    return now;
  }

  async initialize() {
    this.isAuthPending.value = true;
    const authStore = await this.storage.getItem(this.storageKey);
    if (authStore) {
      const { data, token } = JSON.parse(authStore);
      const { exp } = jwtDecode<JwtPayload>(token);
      const now = this.getNow();

      if (exp && exp > now) {
        this.state.data = data;
        this.state.token = token;
        this.onAuthenticationChange(token);
        this.isAuthPending.value = false;

        setTimeout(() => {
          if (this.rootStore.isOnline.value) {
            this.console.log('Init reAuthenticate');
            this.reAuthenticate(false, exp);
          }
        }, 5000);
      } else {
        this.onAuthenticationChange(null);
        this.isAuthPending.value = false;
      }
    }
  }

  save() {
    this.storage.setItem && this.storage.setItem(this.storageKey, JSON.stringify(this.state));
  }

  onAuthenticationChange(token: string | null) {
    this.rootStore.onAuthentication(token);
    this.onAuthentication && this.onAuthentication(token);
    this.rootStore.emitter.emit(AUTH_STATE_CHANGED, token ? true : false);
    if (token === null) {
      this.state.token = null;
      this.state.data = null;
      this.logout();
    } else {
      this.afterAuthenticate(token);
      this.userService &&
        this.userService.find(
          {},
          {
            refresh: true,
          },
        );
    }
  }

  authenticate(email: string, password: string): Promise<any> {
    return new Promise((res, rej) => {
      this.client
        .authenticate({
          strategy: 'local',
          email,
          password,
        })
        .then((resp) => {
          const { accessToken, ...data } = resp;
          this.state.token = accessToken;
          this.state.data = data;
          this.onAuthenticationChange(accessToken);
          this.save();
          this.isAuthPending.value = false;
          res(resp);
        })
        .catch((e: Error) => {
          this.authError.value = e;
          if (this.rootStore.isOnline.value) {
            this.logout();
          }
          rej(e);
        });
    });
  }

  reAuthenticate(force = false, tokenExpires = 0): Promise<any> {
    this.console.time('ReAuthenticate');
    return new Promise((res, rej) => {
      this.client
        .reAuthenticate(force)
        .then((resp) => {
          const { accessToken, ...data } = resp;
          this.state.token = accessToken;
          this.state.data = data;
          this.onAuthenticationChange(accessToken);
          this.save();
          this.isAuthPending.value = false;
          this.console.timeEnd('ReAuthenticate');
          res(resp);
        })
        .catch((e: Error) => {
          console.log(e);
          this.authError.value = e;
          if (this.rootStore.isOnline.value && tokenExpires <= this.getNow()) {
            this.console.log('ReAuthenticateError but will remain loggedin with current token.');
            this.logout();
          } else {
            this.console.log('ReAuthenticateError');
          }
          this.console.timeEnd('ReAuthenticate');
          rej(e);
        });
    });
  }

  logout(): Promise<void> {
    const onLogout = () => {
      this.storage.removeItem && this.storage.removeItem(this.storageKey);
      this.rootStore.emitter.emit(AUTH_STATE_CHANGED, false);
      this.onAuthenticationChange(null);
      this.afterLogout();
    };

    return new Promise((res, rej) => {
      this.beforeLogout();
      this.client
        .logout()
        .then(() => {
          onLogout();
          res();
        })
        .catch(() => {
          onLogout();
          rej();
        });
    });
  }
}
